import React from 'react';
import type { NoteItem, ReminderItem } from '../../domain/types';
import { Button } from '../../ui/common/Button';

interface RadarToolsGridProps {
  reminders: ReminderItem[];
  notes: NoteItem[];
  onOpenTool: (tool: 'daily' | 'notes') => void;
}

export const RadarToolsGrid: React.FC<RadarToolsGridProps> = ({ reminders, notes, onOpenTool }) => {
  const activeReminders = reminders.filter((r) => !r.done);

  return (
    <div data-testid="radar-tools-grid" className="z-radar-tools-grid">
      {/* 1. Daily Standup */}
      <div
        className="z-radar-tool-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '16px 18px',
          borderRadius: 16,
          border: '1px solid var(--z-border)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          minHeight: 180,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(99, 102, 241, 0.1)',
                color: 'var(--z-accent-2)',
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--z-text)' }}>Daily</span>
              <span style={{ fontSize: 11, color: 'var(--z-dim)' }}>Resumen del standup</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--z-text-2)' }}
            >
              <span style={{ fontWeight: 600 }}>Ayer</span>
              <span style={{ color: 'var(--z-dim)' }}>12m</span>
            </div>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--z-text-2)' }}
            >
              <span style={{ fontWeight: 600 }}>Hoy</span>
              <span style={{ color: 'var(--z-dim)' }}>8 temas</span>
            </div>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--z-text-2)' }}
            >
              <span style={{ fontWeight: 600 }}>Bloqueos</span>
              <span style={{ color: 'var(--z-bad)', fontWeight: 600 }}>2 activos</span>
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label="Ver Daily → (abrir Daily standup)"
          onClick={() => onOpenTool('daily')}
          style={{
            padding: 0,
            color: 'var(--z-accent-2)',
            marginTop: 12,
            justifyContent: 'flex-start',
          }}
        >
          Ver Daily →
        </Button>
      </div>

      {/* 2. Recordatorios */}
      <div
        className="z-radar-tool-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '16px 18px',
          borderRadius: 16,
          border: '1px solid var(--z-border)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          minHeight: 180,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#3B82F6',
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--z-text)' }}>
                Recordatorios
              </span>
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 99,
                background: 'rgba(59, 130, 246, 0.12)',
                color: '#3B82F6',
              }}
            >
              {activeReminders.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            {activeReminders.length === 0 ? (
              <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
                Sin recordatorios pendientes
              </span>
            ) : (
              activeReminders.slice(0, 2).map((r, i) => (
                <div key={r.id || i} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontWeight: 700,
                        color: 'var(--z-text)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {r.time || '14:00'}
                    </span>
                    <span
                      style={{
                        color: 'var(--z-dim)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.text}
                    </span>
                  </div>
                  <span style={{ fontSize: 10.5, color: 'var(--z-dim)' }}>
                    {i === 0 ? 'Hoy' : 'Mañana 09:30'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label="Ver todos → (abrir recordatorios)"
          onClick={() => onOpenTool('notes')}
          style={{
            padding: 0,
            color: 'var(--z-accent-2)',
            marginTop: 12,
            justifyContent: 'flex-start',
          }}
        >
          Ver todos →
        </Button>
      </div>

      {/* 3. Notas */}
      <div
        className="z-radar-tool-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '16px 18px',
          borderRadius: 16,
          border: '1px solid var(--z-border)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          minHeight: 180,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: 'var(--z-ok)',
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--z-text)' }}>Notas</span>
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 99,
                background: 'rgba(16, 185, 129, 0.12)',
                color: 'var(--z-ok)',
              }}
            >
              {notes.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            {notes.length === 0 ? (
              <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>Sin notas recientes</span>
            ) : (
              notes.slice(0, 1).map((n, idx) => (
                <React.Fragment key={n.id || idx}>
                  <span style={{ fontWeight: 600, color: 'var(--z-text-2)', lineHeight: 1.35 }}>
                    {n.text || 'Idea para mejorar el flujo de carrier en popup VIN'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--z-dim)' }}>
                    {n.link ? `Relacionado a ${n.link}` : 'Relacionado a DEV-3056'}
                  </span>
                  <span style={{ fontSize: 10.5, color: 'var(--z-dim)', marginTop: 2 }}>
                    Actualizada hace 1h
                  </span>
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label="Abrir nota →"
          onClick={() => onOpenTool('notes')}
          style={{
            padding: 0,
            color: 'var(--z-accent-2)',
            marginTop: 12,
            justifyContent: 'flex-start',
          }}
        >
          Abrir nota →
        </Button>
      </div>
    </div>
  );
};
